import { Alert, AlertStatus } from "@chakra-ui/alert";
import { alertAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { Property } from "csstype";

const { defineMultiStyleConfig } = createMultiStyleConfigHelpers(
  alertAnatomy.keys,
);

interface AlertColors {
  borderColor: Property.BorderColor;
  background: Property.BackgroundColor;
  backgroundActive?: Property.BackgroundColor;
  titleColor: Property.Color;
  descriptionColor: Property.Color;
  iconColor?: Property.Color;
}

const colorsForAlertStatus: Partial<Record<AlertStatus, AlertColors>> = {
  info: {
    borderColor: "blue.300",
    background: "blue.25",
    backgroundActive: "blue.100",
    titleColor: "blue.900",
    descriptionColor: "blue.900",
  },
  error: {
    borderColor: "error.300",
    background: "error.25",
    iconColor: "error.600",
    titleColor: "error.700",
    descriptionColor: "error.700",
  },
  warning: {
    borderColor: "warning.200",
    background: "warning.25",
    titleColor: "warning.900",
    descriptionColor: "warning.900",
  },
};

const colorsForColorScheme: Partial<Record<string, AlertColors>> = {
  gray: {
    borderColor: "gray.200",
    background: "gray.25",
    titleColor: "gray.700",
    descriptionColor: "gray.700",
    iconColor: "gray.500",
  },
};

const AlertTheme = defineMultiStyleConfig({
  baseStyle: (props) => {
    const {
      borderColor,
      background,
      backgroundActive,
      titleColor,
      descriptionColor,
    } =
      colorsForColorScheme[props.colorScheme] ??
      colorsForAlertStatus[props.status as AlertStatus] ??
      {};
    return {
      container: {
        flexShrink: 0,
        borderWidth: 1,
        borderRadius: "8px",
        borderColor,
        background,
        "& a": {
          textStyle: "bodyBold",
          textDecoration: "underline",
          whiteSpace: "nowrap",
          color: titleColor,
          flex: "inherit",
          _hover: {
            color: titleColor,
            textDecorationColor: titleColor,
          },
          _active: {
            opacity: 0.5,
          },
        },
        ...(props.onClick
          ? ({
              cursor: "pointer",
              _active: {
                bg: backgroundActive,
              },
            } as const)
          : undefined),
      },
      title: {
        color: titleColor,
        fontSize: "16px",
        fontWeight: "semibold",
        flexGrow: 1,
      },
      description: {
        color: descriptionColor,
        flexGrow: 1,
        lineHeight: "20px",
        whiteSpace: "pre-wrap",
      },
      icon: {
        color: titleColor,
        margin: 0,
      },
    };
  },
  sizes: {
    sm: {
      container: {
        p: "8px",
        borderRadius: "4px",
        alignItems: "flex-start",
        "& a": {
          pl: "8px",
        },
      },
      description: {
        lineHeight: "20px",
      },
      icon: {
        width: "16px",
        height: "18px",
        // icon is before title or description
        "&:has(+ .chakra-alert__title), &:has(+ .chakra-alert__desc)": {
          marginTop: "2px",
          marginRight: "8px",
        },
      },
    },
    md: {
      container: {
        py: "11px",
        "& a": {
          pl: "8px",
        },
      },
      icon: {
        width: "16px",
        height: "16px",
        // icon is before title or description
        "&:has(+ .chakra-alert__title), &:has(+ .chakra-alert__desc)": {
          marginRight: "8px",
        },
        "& > *": {
          width: "16px !important",
          height: "16px !important",
        },
      },
    },
  },
  defaultProps: {
    size: "md",
  },
});

Alert.defaultProps = {
  status: "info",
};

export default AlertTheme;
